import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { User, Mail, Phone, Camera, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export const UserProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phoneNumber: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsEditing(false);
    showSuccess('Profile updated successfully!');
  };

  if (!user) {
    return <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center text-gray-400 font-bold uppercase tracking-widest text-xs">Accessing profile...</div>;
  }

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">Identity Center</h1>
          <p className="text-gray-500 font-medium tracking-tight">Manage your personal signature and account security.</p>
        </div>
        <Link to="/" className="text-sm font-bold text-primary flex items-center gap-2 hover:gap-3 transition-all mb-1">
          <ArrowLeft className="size-4" /> Return Home
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        {/* Profile Sidebar */}
        <div className="lg:col-span-4 lg:sticky lg:top-24 h-fit">
          <div className="bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] p-10 flex flex-col items-center text-center space-y-6 border border-border/40">
            <div className="relative group">
              <div className="size-32 md:size-40 rounded-[2.5rem] overflow-hidden bg-white dark:bg-black border border-border/40 shadow-xl flex items-center justify-center">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="size-full object-cover transition-transform group-hover:scale-110 duration-500" />
                ) : (
                  <span className="text-4xl font-black text-primary">{user.name?.charAt(0)}</span>
                )}
              </div>
              <button className="absolute bottom-2 right-2 size-10 bg-primary text-white rounded-xl shadow-lg border-4 border-gray-50 dark:border-gray-900 flex items-center justify-center hover:scale-110 transition-transform">
                <Camera className="size-4" />
              </button>
            </div>
            <div className="space-y-1">
              <h2 className="text-xl font-black text-[#111418] dark:text-white uppercase tracking-tight">{user.name}</h2>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Premium Member</p>
            </div>
            <div className="w-full pt-4">
              <div className="flex items-center gap-3 p-4 bg-white dark:bg-black rounded-2xl border border-border/40">
                <ShieldCheck className="size-5 text-emerald-500" />
                <div className="text-left">
                  <p className="text-[10px] font-black uppercase tracking-widest text-[#111418] dark:text-white leading-none mb-1">Authenticated</p>
                  <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">Global Security Protocol Active</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Forms */}
        <div className="lg:col-span-8 space-y-10">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1 flex items-center gap-2">
                  <User className="size-3" /> Full Identity
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!isEditing}
                  className="h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold focus-visible:ring-2 focus-visible:ring-primary/10 transition-all disabled:opacity-50"
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1 flex items-center gap-2">
                  <Mail className="size-3" /> Communication Portal
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  disabled
                  className="h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold cursor-not-allowed opacity-50"
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-1 flex items-center gap-2">
                  <Phone className="size-3" /> Secure Line
                </label>
                <Input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  disabled={!isEditing}
                  placeholder="No line connected"
                  className="h-14 bg-gray-50 dark:bg-gray-900 border-none rounded-2xl px-6 font-bold focus-visible:ring-2 focus-visible:ring-primary/10 transition-all disabled:opacity-50"
                />
              </div>
            </div>

            <Separator className="opacity-50" />

            {isEditing ? (
              <div className="flex items-center gap-4">
                <Button type="submit" className="h-14 px-10 rounded-2xl font-black bg-primary hover:bg-primary/90 text-white shadow-xl shadow-primary/20">
                  Save Changes
                </Button>
                <Button type="button" variant="ghost" onClick={() => setIsEditing(false)} className="h-14 px-8 rounded-2xl font-black text-gray-400 uppercase tracking-widest text-[10px] hover:bg-gray-50 dark:hover:bg-gray-900">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button type="button" onClick={() => setIsEditing(true)} className="h-14 px-10 rounded-2xl font-black bg-[#111418] dark:bg-white text-white dark:text-black shadow-xl transition-all hover:-translate-y-1">
                Edit Identification
              </Button>
            )}
          </form>

          <div className="p-10 bg-gray-50 dark:bg-gray-900 rounded-[2.5rem] border border-border/40 space-y-6">
            <h3 className="text-xl font-black tracking-tight text-[#111418] dark:text-white uppercase">Security Archive</h3>
            <p className="text-sm font-medium text-gray-400 leading-relaxed">Your account is fortified with cryptographic security. To modify advanced settings or manage linked accounts, please visit the global security center.</p>
            <Button variant="outline" className="rounded-xl font-black text-[10px] uppercase tracking-widest px-6 h-10 border-border/60 hover:bg-white dark:hover:bg-black transition-colors">
              Access Security Center
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
